import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark, faPenToSquare} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";

interface Props {
  UploadCompletion: (val:boolean)=>void;
  token: string;
  updateReady: boolean;
  storedMediaFiles?: string[];
  setStoredMediaFiles?: React.Dispatch<React.SetStateAction<string[]>>;
}

export default function MultiMediaUploader({UploadCompletion, token, updateReady, storedMediaFiles, setStoredMediaFiles}: Props) {
  const [previews, setPreviews] = useState<(string | null)[]>([
    null, null, null, null, null, null
  ]);

  const [files, setFiles] = useState<(File | null)[]>([
    null, null, null, null, null, null
  ]);

  const handleFileChange = (slot: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isImageSlot = slot < 5; // first 4 are image
    const isValidImage = ["image/jpeg", "image/png", "image/jpg"].includes(file.type);
    const isValidVideo = file.type.startsWith("video/");

    // IMAGE VALIDATION
    if (isImageSlot && !isValidImage) {
        alert("Only JPG, PNG allowed for photos");
        return;
    }

    // VIDEO VALIDATION
    if (!isImageSlot && !isValidVideo) {
        alert("Must upload a valid MP4 video");
        return;
    }

    // Video duration enforcement (slot 5 only)
    if (!isImageSlot) {
        const video = document.createElement("video");
        video.preload = "metadata";
        video.src = URL.createObjectURL(file);

        video.onloadedmetadata = () => {
          const duration = video.duration;
          URL.revokeObjectURL(video.src);

          // enforce maximum length
          if (duration > 15) {
              alert("Video cannot be longer than 15 seconds.");
              return;
          }

          // only set state AFTER validation
          updateFileAndPreview(slot, file);
        };

        return; 
    }

    // If image slot → just update
    updateFileAndPreview(slot, file);
    };

    const updateFileAndPreview = (slot: number, file: File) => {
      const url = URL.createObjectURL(file);

      setFiles(prev => {
          const updated = [...prev];
          updated[slot] = file;
          return updated;
      });

      setPreviews(prev => {
          const updated = [...prev];
          updated[slot] = url;
          return updated;
      });
    };

  useEffect(()=>{
    if (!storedMediaFiles) return;
 
    setPreviews(prev => {
      const next = [...prev];

      storedMediaFiles.forEach((file, index) => {
        if (file) {
          next[index] = file;
        }
      });
      return next;
    })
    
  },[storedMediaFiles])

  function imageUploader(file:any, position?: string){
    const formData = new FormData();
    formData.append("filepath", file);
    if (position !== undefined) {
        formData.append("position", String(position))
    }
    const endpoint = position ? `image/upload_image/?position=${position}` : 'image/upload_image/'
    return axios.post(`${process.env.REACT_APP_SKILLJA_URL}/${endpoint}`, formData, { 
        headers: {
            'X-CSRFToken': token,
        },
        withCredentials: true
    }) 
        .then(res => {
            if (res.status !== 200) {
                console.error("Failed to retrieve profile details")
            }
        })
        .catch(error => {console.error(error)})
  }

  function videoUploader(file:any){
    const formData = new FormData();
    formData.append("filepath", file);

    return axios.post(`${process.env.REACT_APP_SKILLJA_URL}/image/upload_video/`, formData, { 
        headers: {
            'X-CSRFToken': token,
        },
        withCredentials: true
    }) 
        .then(res => {
            if (res.status !== 200) {
                console.error("Failed to retrieve profile details")
            }
        })
        .catch(error => {console.error(error)})
  }

  // upload media when profile changes are saved
  useEffect(() => {
    if (!updateReady) return;
    
    // Only upload files that exist
    const validFiles = [...files]
    const formatIndex = ['two','three','four', 'club', 'video']
    const uploadTasks = validFiles.map((file, index) => {
      if (!file) return null;
      if (index < 5) return index === 0 ? imageUploader(file) : imageUploader(file, formatIndex[index-1]);
      if (index === 5) return videoUploader(file);
      return null;
    });

    Promise.all(uploadTasks)
      .then(() => UploadCompletion(true))
      .catch(() => UploadCompletion(false));
    
  }, [updateReady]);

function removeMedia(position: string) {
  const indexPositions = ['one', 'two', 'three', 'four', 'club', 'video'];
  const index = indexPositions.indexOf(position);
  if (index === -1) return;

  // Check if there’s an existing file before clearing
  const hasExistingFile = !!previews[index];

  // Clear the preview
  setPreviews(prev => {
    const updated = [...prev];
    updated[index] = null;
    return updated;
  });

  // Only attempt server delete if there was a file
  if (!hasExistingFile) return;

  // Delay deletion slightly (optional)
  setTimeout(() => {
    axios
      .post(
        `${process.env.REACT_APP_SKILLJA_URL}/image/delete_media/`,
        { position },
        {
          headers: { 'X-CSRFToken': token },
          withCredentials: true,
        }
      )
      .then(res => {
        if (res.status !== 200) {
          window.alert('Oops! Something went wrong');
        }
      })
      .catch(error => console.error(error));
  }, 500); // 0.5s delay to let UI update
}



  return (
    <div className="flex flex-col justify-center items-center my-5">
      <div className="flex justify-evenly w-full">

        {/* LEFT SIDE: 4 IMAGES */}
        <section>
          <div className="grid grid-cols-2 grid-rows-2 gap-x-7 gap-y-5">

            {/* Slot 0 — Primary */}
            <div className="relative">
              {previews[0] && <FontAwesomeIcon icon={faCircleXmark} className="text-red-600 text-2xl absolute -right-3.5 -top-2.5 z-10 cursor-pointer hover:text-red-700" onClick={()=>removeMedia('one')}/> }
              <div
                className="relative hover:outline hover:outline-main-green-500 hover:rounded-xl cursor-pointer"
                onClick={() => document.getElementById("slot0")?.click()}
              >
                <p className="absolute right-2 top-2 font-semibold text-main-grey-200 text-sm">
                  Primary
                </p>
                {!previews[0] && <FontAwesomeIcon icon={faPenToSquare} className="text-main-grey-200 text-xl absolute right-2 bottom-2" />}
                <img
                  src={previews[0] || require("../../assets/default-avatar.jpg")}
                  className="w-28 h-28 rounded-xl object-cover"
                  alt="Preview Slot 0"
                />
                <input
                  type="file"
                  id="slot0"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileChange(0, e)}
                />
              </div>
            </div>

            {/* Slot 1 */}
            <div className="relative">
              {previews[1] && <FontAwesomeIcon icon={faCircleXmark} className="text-red-600 text-2xl absolute -right-3.5 -top-2.5 z-10 cursor-pointer hover:text-red-700" onClick={()=>removeMedia('two')}/>}
              <div
                className="relative hover:outline hover:outline-main-green-500 hover:rounded-xl cursor-pointer"
                onClick={() => document.getElementById("slot1")?.click()}
              >
                <p className="absolute right-3 top-2 font-semibold text-main-grey-200">2</p>
                {!previews[1] && <FontAwesomeIcon icon={faPenToSquare} className="text-main-grey-200 text-xl absolute right-2 bottom-2" />}
                <img
                  src={previews[1] || require("../../assets/default-avatar.jpg")}
                  className="w-28 h-28 rounded-xl"
                  alt="Preview Slot 1"
                />
                <input
                  type="file"
                  id="slot1"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileChange(1, e)}
                />
              </div>
            </div>

            {/* Slot 2 */}
            <div className="relative">
              {previews[2] && <FontAwesomeIcon icon={faCircleXmark} className="text-red-600 text-2xl absolute -right-3.5 -top-2.5 z-10 cursor-pointer hover:text-red-700" onClick={()=>removeMedia('three')}/>}
              <div
                className="relative hover:outline hover:outline-main-green-500 hover:rounded-xl cursor-pointer"
                onClick={() => document.getElementById("slot2")?.click()}
              >
                <p className="absolute right-3 top-2 font-semibold text-main-grey-200">3</p>
                {!previews[2] &&<FontAwesomeIcon icon={faPenToSquare} className="text-main-grey-200 text-xl absolute right-2 bottom-2" />}
                <img
                  src={previews[2] || require("../../assets/default-avatar.jpg")}
                  className="w-28 h-28 rounded-xl"
                  alt="Preview Slot 2"
                />
                <input
                  type="file"
                  id="slot2"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileChange(2, e)}
                />
              </div>
            </div>

            {/* Slot 3 */}
            <div className="relative">
              {previews[3] && <FontAwesomeIcon icon={faCircleXmark} className="text-red-600 text-2xl absolute -right-3.5 -top-3.5 z-10 cursor-pointer hover:text-red-700" onClick={()=>removeMedia('four')}/>}
              <div
                className="relative hover:outline hover:outline-main-green-500 hover:rounded-xl cursor-pointer"
                onClick={() => document.getElementById("slot3")?.click()}
              >
                <p className="absolute right-3 top-2 font-semibold text-main-grey-200">4</p>
                {!previews[3] && <FontAwesomeIcon icon={faPenToSquare} className="text-main-grey-200 text-xl absolute right-2 bottom-2" /> }
                <img
                  src={previews[3] || require("../../assets/default-avatar.jpg")}
                  className="w-28 h-28 rounded-xl"
                  alt="Preview Slot 3"
                />
                <input
                  type="file"
                  id="slot3"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileChange(3, e)}
                />
              </div>
            </div>
          </div>
          <p className="mt-4 text-xs text-center text-gray-500">
            PNG, JPG or JPEG Allowed<br/> (Recommended size: 500px by 500px)
          </p>
        </section>

        {/* RIGHT SIDE: VIDEO & CLUB SLOT */}
        <section className="flex flex-col items-center">
          <div className="flex flex-col items-center">
            <div className="relative">
              {previews[5] && <FontAwesomeIcon icon={faCircleXmark} className="text-red-600 text-2xl absolute -right-3.5 -top-2.5 z-10 cursor-pointer hover:text-red-700" onClick={()=>removeMedia('video')}/>}
              <div
                className="relative hover:outline hover:outline-main-green-500 hover:rounded-xl cursor-pointer"
                onClick={() => document.getElementById("slot5")?.click()}
              >
                {!previews[5] && <FontAwesomeIcon icon={faPenToSquare} className="text-main-grey-200 text-xl absolute right-2 bottom-2"/>}
                {previews[5] ?
                    <video
                        src={previews[5]}
                        className="w-24 h-24 rounded-xl object-cover outline outline-main-grey-100"
                        muted
                    />
                :
                    <img
                        src={require("../../assets/default-video-avatar.png")}
                        className="w-24 h-24 rounded-xl"
                    />
                }
                <input
                  type="file"
                  id="slot5"
                  accept="video/mp4,video/*"
                  className="hidden"
                  onChange={(e) => handleFileChange(5, e)}
                />
              </div>
            </div>
            <p className="mt-3 text-xs text-center text-main-grey-300">
              MP4 Allowed (15 Seconds Max.)
            </p>
          </div>
          <div className="flex flex-col items-center mt-6">
            <div className="relative">
              {previews[4] && <FontAwesomeIcon icon={faCircleXmark} className="text-red-600 text-2xl absolute -right-3.5 -top-2.5 z-10 cursor-pointer hover:text-red-700" onClick={()=>removeMedia('club')}/>}
              <div
                  className="relative hover:outline hover:outline-main-green-500 hover:rounded-xl cursor-pointer"
                  onClick={() => document.getElementById("slot4")?.click()}
                >
                  <p className="absolute right-3 top-2 font-semibold text-main-grey-200 text-sm">Employer</p>
                  {!previews[4] &&<FontAwesomeIcon icon={faPenToSquare} className="text-main-grey-200 text-xl absolute right-2 bottom-2" />}
                  <img
                    src={previews[4] || require("../../assets/default-avatar.jpg")}
                    className="w-24 h-24 rounded-xl"
                    alt="Preview Slot 4"
                  />
                  <input
                    type="file"
                    id="slot4"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFileChange(4, e)}
                  />
              </div>
            </div>
            <p className="mt-4 text-xs text-center text-gray-500">
              PNG, JPG or JPEG Allowed<br/> (Recommended size: 300px by 150px)
            </p>
          </div>
        </section>
      </div>

      <div className="w-full h-1 mt-6 mb-6 bg-main-grey-100 rounded-full" />
    </div>
  )
}